import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Ticket, AlertCircle } from 'lucide-react';
import { adminApi } from '../../../services/api';
import { TargetSelector } from './TargetSelector';
import { NotificationBlock, defaultNotificationState } from './NotificationBlock';

const COUPON_TYPE_LABELS = {
  discount: 'Sconto %',
  bonus_points: 'Punti bonus',
  prize: 'Premio',
  game_token: 'Gettoni',
  voucher: 'Buono €',
};

function couponBenefit(c) {
  if (!c) return '';
  if (c.coupon_type === 'discount') return `Sconto ${c.discount_percent}%`;
  if (c.coupon_type === 'bonus_points') return `+${c.bonus_points} punti`;
  if (c.coupon_type === 'prize') return c.prize_name || 'Premio';
  if (c.coupon_type === 'game_token') return `+${c.tokens_amount} gettoni`;
  if (c.coupon_type === 'voucher') return `Buono ${Number(c.voucher_amount || 0).toFixed(2)}€`;
  return c.coupon_type;
}

export function GiftCouponTab({ ctrl }) {
  const [coupons, setCoupons] = useState([]);
  const [couponId, setCouponId] = useState('');
  const [note, setNote] = useState('');
  const [notif, setNotif] = useState(defaultNotificationState);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const updateNotif = (k, v) => setNotif(s => ({ ...s, [k]: v }));

  // Load brand coupons (active only)
  useEffect(() => {
    adminApi.get('/admin/coupons?is_active=true&limit=200').then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.coupons || []);
      setCoupons(list);
    }).catch(() => setCoupons([]));
  }, []);

  const fetchPreview = useCallback(async () => {
    if (!couponId) { setPreview(null); return; }
    try {
      const body = { coupon_id: couponId, ...ctrl.targetPayload };
      const res = await adminApi.post('/admin/gift-coupon/preview', body);
      setPreview(res.data);
    } catch { setPreview(null); }
  }, [couponId, ctrl.targetPayload]);

  useEffect(() => {
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [fetchPreview]);

  const handleSend = async () => {
    if (!couponId) { toast.error('Seleziona un coupon'); return; }
    if (!ctrl.isValid) { toast.error('Seleziona un cliente'); return; }
    if (notif.notify && notif.channels.length === 0) {
      toast.error('Scegli almeno un canale di notifica oppure disattiva la notifica');
      return;
    }
    setSending(true);
    try {
      const body = {
        coupon_id: couponId, note,
        notify: notif.notify,
        channels: notif.notify ? notif.channels : [],
        custom_message: notif.notify ? notif.customMessage : '',
        custom_title: notif.notify ? notif.customTitle : '',
        ...ctrl.targetPayload,
      };
      const res = await adminApi.post('/admin/gift-coupon', body);
      const skipMsg = res.data.skipped_count > 0 ? ` (${res.data.skipped_count} saltati per requisiti coupon)` : '';
      toast.success(`${res.data.message}${skipMsg}`);
      setNote('');
      if (ctrl.state.mode === 'single') ctrl.actions.clearSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setSending(false); }
  };

  const selectedCoupon = preview?.coupon || coupons.find(c => c.id === couponId);
  const eligibleStr = preview
    ? (ctrl.state.mode === 'single'
        ? (preview.eligible_count > 0 ? '1 idoneo' : '0 idonei (cliente non soddisfa i requisiti)')
        : `${preview.eligible_count} idonei su ${preview.total}` + (preview.ineligible_count > 0 ? ` (${preview.ineligible_count} esclusi)` : ''))
    : '';

  return (
    <div data-testid="gift-coupon-tab">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-4">
        <label className="text-xs text-[#64748B] font-medium block mb-1.5">Coupon da regalare</label>
        {coupons.length === 0 ? (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>
              Non hai ancora nessun coupon attivo. Crealo prima in <a href="/admin/coupons" className="font-semibold underline">Coupon</a>, poi torna qui per regalarlo.
            </div>
          </div>
        ) : (
          <select value={couponId} onChange={e => setCouponId(e.target.value)}
            data-testid="gift-coupon-select"
            className="w-full h-12 bg-slate-50 border border-slate-200 rounded-xl px-4 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
            <option value="">— Scegli un coupon attivo —</option>
            {coupons.map(c => (
              <option key={c.id} value={c.id}>
                {c.code} · {COUPON_TYPE_LABELS[c.coupon_type] || c.coupon_type} · {c.description?.slice(0, 40) || ''}
              </option>
            ))}
          </select>
        )}

        {selectedCoupon && (
          <div className="mt-3 p-4 rounded-xl bg-[#D44E20]/5 border border-[#D44E20]/20" data-testid="gift-coupon-preview-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#D44E20] flex items-center justify-center shrink-0">
                <Ticket size={18} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#1A1A1A] truncate">{selectedCoupon.code}</p>
                <p className="text-xs text-[#64748B]">{COUPON_TYPE_LABELS[selectedCoupon.coupon_type]} · {couponBenefit(selectedCoupon)}</p>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#64748B] flex flex-wrap gap-x-3 gap-y-1">
              {selectedCoupon.min_tier && selectedCoupon.min_tier !== 'bronze' && <span>Tier min: {selectedCoupon.min_tier}</span>}
              {selectedCoupon.min_age > 0 && <span>Età min: {selectedCoupon.min_age}+</span>}
              {selectedCoupon.valid_until && <span>Scade: {String(selectedCoupon.valid_until).slice(0, 10)}</span>}
            </div>
            {selectedCoupon.description && <p className="mt-2 text-xs text-[#1A1A1A]">{selectedCoupon.description}</p>}
          </div>
        )}

        <div className="mt-4">
          <label className="text-xs text-[#64748B] font-medium block mb-1.5">Nota interna (opzionale)</label>
          <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Es. Recupero clienti inattivi"
            data-testid="gift-coupon-note-input"
            className="w-full h-12 bg-slate-50 border border-slate-200 rounded-xl px-4 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
        </div>
      </div>

      <TargetSelector ctrl={ctrl} />
      <NotificationBlock value={notif} onChange={updateNotif} kind="coupon" />

      <button onClick={handleSend}
        disabled={!couponId || sending || (ctrl.state.mode === 'single' && !ctrl.state.searchResult)}
        data-testid="gift-coupon-send-btn"
        className="w-full h-12 bg-[#D44E20] text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#C04018] transition-colors">
        {sending ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Ticket size={18} />}
        {sending ? 'Invio in corso...' : `Regala coupon${eligibleStr ? ` · ${eligibleStr}` : ''}`}
      </button>
    </div>
  );
}
